import {
  getBackendApiUrl,
  getRegionPin,
  getRegionPinTimestamp,
  persistRegionPin,
  type RegionPin,
} from "../config/backend";
import type { RegionsHealth, RegionStatus } from "../hooks/useRegionHealth";

const REGION_PIN_STORAGE_KEY = "traci-api-region-override";

/**
 * TRAC-550 B4: Pin-flip state machine.
 *
 * Consumes the per-region health snapshot produced by useRegionHealth (B3)
 * and decides whether to:
 *   - flip the user's pin to the surviving region
 *   - unflip back to auto (Global Accelerator)
 *   - emit a "both-unhealthy" signal (engages emergency overlay via a
 *     separate path — Josh's reconciliation extended in B7)
 *
 * The decision logic lives in `evaluateFailover` as a pure function so it can
 * be exhaustively tested. The wrapper service holds the evolving context
 * (last-flip-time, both-healthy-since, etc.) and fans actions out to
 * subscribers.
 */

// --- Tunables ---------------------------------------------------------------

/** Consecutive unhealthy responses needed to trigger a flip. */
const FLIP_THRESHOLD = 2;
/** Target region must have responded healthy within this window to be considered alive. */
const FRESHNESS_MS = 30_000;
/** Minimum gap between flips (anti-flap). */
const DWELL_MS = 60_000;
/** Pin TTL — older than this on app load reverts to auto. */
const PIN_TTL_MS = 60 * 60 * 1000;
/** Both regions must stay healthy continuously for this duration before unflipping. */
const RECOVERY_MS = 2 * 60_000;
/** Minimum consecutive both-healthy poll count before unflipping (per adversarial-agent finding). */
const UNFLIP_HYSTERESIS = 4;

// --- Types ------------------------------------------------------------------

export type FailoverAction =
  | { type: "no-op" }
  | { type: "flip"; from: RegionPin; to: "us-east-1" | "us-west-2"; reason: string }
  | { type: "unflip"; from: RegionPin; reason: string }
  | { type: "both-unhealthy" };

export interface FailoverContext {
  /** Current pin (read from localStorage on init, updated on every flip/unflip). */
  currentPin: RegionPin;
  /** Time of last flip — used to enforce the dwell rule. */
  lastFlipAt: Date | null;
  /** Timestamp from which both regions have been continuously healthy, or null. */
  bothHealthySince: Date | null;
  /** Consecutive both-healthy polls in a row (resets when either region != healthy). */
  bothHealthyConsecutive: number;
}

export type Listener = (action: FailoverAction) => void;

// --- Pure decision function -------------------------------------------------

function isFresh(status: RegionStatus, now: Date): boolean {
  return status.lastSeen !== null && now.getTime() - status.lastSeen.getTime() < FRESHNESS_MS;
}

function dwellSatisfied(ctx: FailoverContext, now: Date): boolean {
  if (ctx.lastFlipAt === null) return true;
  return now.getTime() - ctx.lastFlipAt.getTime() >= DWELL_MS;
}

export function evaluateFailover(
  health: RegionsHealth,
  ctx: FailoverContext,
  now: Date = new Date(),
): FailoverAction {
  const { usEast1, usWest2 } = health;
  const eastUnhealthy = usEast1.state === "unhealthy" && usEast1.consecutive >= FLIP_THRESHOLD;
  const westUnhealthy = usWest2.state === "unhealthy" && usWest2.consecutive >= FLIP_THRESHOLD;
  const eastHealthyAndFresh = usEast1.state === "healthy" && isFresh(usEast1, now);
  const westHealthyAndFresh = usWest2.state === "healthy" && isFresh(usWest2, now);

  // Both-unhealthy precedence: even if dwell isn't satisfied, this is the
  // signal that goes out for the emergency-overlay path.
  if (eastUnhealthy && westUnhealthy) {
    return { type: "both-unhealthy" };
  }

  if (!dwellSatisfied(ctx, now)) {
    return { type: "no-op" };
  }

  // Flip: current region is unhealthy AND target is alive. Works from auto
  // too — when GA is routing the user to a region that just died, we lock
  // them onto the survivor explicitly.
  const pinned = ctx.currentPin;
  if (
    (pinned === "us-east-1" || pinned === "auto") &&
    eastUnhealthy &&
    westHealthyAndFresh
  ) {
    return {
      type: "flip",
      from: pinned,
      to: "us-west-2",
      reason: `us-east-1 unhealthy ${usEast1.consecutive}× consecutive`,
    };
  }
  if (
    (pinned === "us-west-2" || pinned === "auto") &&
    westUnhealthy &&
    eastHealthyAndFresh
  ) {
    return {
      type: "flip",
      from: pinned,
      to: "us-east-1",
      reason: `us-west-2 unhealthy ${usWest2.consecutive}× consecutive`,
    };
  }

  // Unflip: only relevant if currently pinned. Both regions must have been
  // continuously healthy for ≥RECOVERY_MS AND ≥UNFLIP_HYSTERESIS consecutive
  // polls — the dual rule prevents racing the AWS-metric alarm cool-down.
  if (pinned !== "auto") {
    const recoveryMet =
      ctx.bothHealthySince !== null &&
      now.getTime() - ctx.bothHealthySince.getTime() >= RECOVERY_MS &&
      ctx.bothHealthyConsecutive >= UNFLIP_HYSTERESIS;
    if (recoveryMet) {
      return {
        type: "unflip",
        from: pinned,
        reason: `both regions healthy ≥${RECOVERY_MS / 60_000}min`,
      };
    }
  }

  return { type: "no-op" };
}

// --- TTL / startup logic ----------------------------------------------------

/**
 * Returns the pin we should be on at app load. Reverts stale pins (older than
 * PIN_TTL_MS) and pins where the most recent poll for the pinned region is
 * unhealthy — both signal that the original reason for the pin is stale.
 *
 * Caller is expected to apply the result via persistRegionPin() if it differs
 * from the current pin.
 */
export function evaluatePinOnLoad(
  currentPin: RegionPin,
  pinTimestamp: Date | null,
  health: RegionsHealth | null,
  now: Date = new Date(),
): RegionPin {
  if (currentPin === "auto") return "auto";
  if (pinTimestamp === null) return currentPin;
  if (now.getTime() - pinTimestamp.getTime() >= PIN_TTL_MS) return "auto";
  if (health !== null) {
    const pinnedStatus = currentPin === "us-east-1" ? health.usEast1 : health.usWest2;
    if (pinnedStatus.state === "unhealthy") return "auto";
  }
  return currentPin;
}

// --- Stateful service wrapper ----------------------------------------------

class RegionFailoverServiceImpl {
  private listeners: Set<Listener> = new Set();
  private ctx: FailoverContext = {
    currentPin: "auto",
    lastFlipAt: null,
    bothHealthySince: null,
    bothHealthyConsecutive: 0,
  };
  // TRAC-550 B6: kill switch. False disables the state machine entirely;
  // applyHealth() returns no-op without touching localStorage or listeners.
  // Toggled by consumers reading /api/settings/announcement.failover_enabled.
  private failoverEnabled: boolean = true;
  private storageListener: ((e: StorageEvent) => void) | null = null;

  subscribe(listener: Listener): () => void {
    this.listeners.add(listener);
    return () => {
      this.listeners.delete(listener);
    };
  }

  /** Re-read pin from localStorage. Call on app boot before applyHealth(). */
  init(): void {
    this.ctx.currentPin = getRegionPin();
  }

  /** Apply TTL + stale-on-pinned-unhealthy logic at app boot. */
  reconcileOnLoad(health: RegionsHealth | null, now: Date = new Date()): void {
    const next = evaluatePinOnLoad(getRegionPin(), getRegionPinTimestamp(), health, now);
    if (next !== this.ctx.currentPin) {
      persistRegionPin(next);
      this.ctx.currentPin = next;
    }
  }

  /** TRAC-550 B6: kill-switch setter. Pump from /api/settings/announcement. */
  setFailoverEnabled(enabled: boolean): void {
    this.failoverEnabled = enabled;
  }

  /** Pump a fresh health snapshot through the state machine. */
  applyHealth(health: RegionsHealth, now: Date = new Date()): FailoverAction {
    if (!this.failoverEnabled) {
      return { type: "no-op" };
    }
    this.updateBothHealthyTracking(health, now);
    const action = evaluateFailover(health, this.ctx, now);

    if (action.type === "flip") {
      persistRegionPin(action.to);
      this.ctx.currentPin = action.to;
      this.ctx.lastFlipAt = now;
      this.ctx.bothHealthySince = null;
      this.ctx.bothHealthyConsecutive = 0;
      this.emit(action);
      this.reportFlipTelemetry(action.from, action.to, action.reason);
    } else if (action.type === "unflip") {
      persistRegionPin("auto");
      this.ctx.currentPin = "auto";
      this.ctx.lastFlipAt = now;
      this.emit(action);
      this.reportFlipTelemetry(action.from, "auto", action.reason);
    } else if (action.type === "both-unhealthy") {
      this.emit(action);
    }

    return action;
  }

  /**
   * TRAC-550 B6: install a window storage listener so a flip in tab A is
   * observed by tab B as if tab B had decided to flip itself. Tab B aborts
   * its open SSE (via the listener subscription chain) and the next message
   * lands on the new region. Without this, two open tabs would diverge —
   * tab A flipped, tab B still streaming against the dead region.
   *
   * Idempotent — safe to call multiple times.
   */
  installStorageListener(): void {
    if (typeof window === "undefined" || this.storageListener !== null) return;
    this.storageListener = (event: StorageEvent) => {
      if (event.key !== REGION_PIN_STORAGE_KEY) return;
      // Kill switch must silence ALL tabs, not just whichever one decided to
      // flip. If another tab persisted a pin before the flag was disabled
      // (or the flag toggled mid-session), this tab still receives the
      // storage event — ignore it the same way applyHealth() does.
      if (!this.failoverEnabled) return;
      const newPin: RegionPin =
        event.newValue === "us-east-1" || event.newValue === "us-west-2"
          ? event.newValue
          : "auto";
      const previousPin = this.ctx.currentPin;
      if (previousPin === newPin) return;

      this.ctx.currentPin = newPin;
      this.ctx.lastFlipAt = new Date();
      this.ctx.bothHealthySince = null;
      this.ctx.bothHealthyConsecutive = 0;

      // Emit as a regular flip so chatAgentService aborts its SSE — the
      // event semantics are the same regardless of WHO decided to flip.
      if (newPin === "auto") {
        this.emit({ type: "unflip", from: previousPin, reason: "external (other tab)" });
      } else {
        this.emit({
          type: "flip",
          from: previousPin,
          to: newPin,
          reason: "external (other tab)",
        });
      }
    };
    window.addEventListener("storage", this.storageListener);
  }

  /**
   * TRAC-550 B6: fire-and-forget telemetry. Lets us answer "did pin-flip
   * actually happen during a real incident?" — the failover system itself
   * needs observability separate from the per-region health metrics it
   * consumes. Failures are silent on purpose; a failed POST must never
   * block the user-facing flip.
   */
  private reportFlipTelemetry(
    from: RegionPin,
    to: "auto" | "us-east-1" | "us-west-2",
    reason: string,
  ): void {
    if (typeof fetch === "undefined") return;
    try {
      fetch(`${getBackendApiUrl()}/api/telemetry/pin-flip`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        credentials: "omit",
        body: JSON.stringify({ from_region: from, to_region: to, reason }),
      }).catch(() => {
        /* fire-and-forget */
      });
    } catch {
      /* fire-and-forget */
    }
  }

  private updateBothHealthyTracking(health: RegionsHealth, now: Date): void {
    const both = health.usEast1.state === "healthy" && health.usWest2.state === "healthy";
    if (both) {
      this.ctx.bothHealthyConsecutive += 1;
      if (this.ctx.bothHealthySince === null) {
        this.ctx.bothHealthySince = now;
      }
    } else {
      this.ctx.bothHealthyConsecutive = 0;
      this.ctx.bothHealthySince = null;
    }
  }

  private emit(action: FailoverAction): void {
    for (const l of this.listeners) {
      try {
        l(action);
      } catch {
        // listener errors must not break the chain
      }
    }
  }

  /** Test-only escape hatch for context inspection / reset. */
  _getContext(): Readonly<FailoverContext> {
    return { ...this.ctx };
  }

  _resetForTests(initial?: Partial<FailoverContext>): void {
    this._setContextForTests(initial);
    this.listeners.clear();
  }

  /** Reset context without touching subscribers — for cross-service integration tests. */
  _setContextForTests(initial?: Partial<FailoverContext>): void {
    this.ctx = {
      currentPin: "auto",
      lastFlipAt: null,
      bothHealthySince: null,
      bothHealthyConsecutive: 0,
      ...initial,
    };
    this.failoverEnabled = true;
  }

  /** Test-only escape hatch to detach the storage listener. */
  _uninstallStorageListenerForTests(): void {
    if (this.storageListener && typeof window !== "undefined") {
      window.removeEventListener("storage", this.storageListener);
    }
    this.storageListener = null;
  }
}

export const regionFailoverService = new RegionFailoverServiceImpl();
